import {
  ContestShapefileRow,
  ContestShapefileStatus
} from '../../app/feathers/contest-shapefiles';
import { District } from '../../app/feathers/districts/District';

export const ACTIVE_CONTEST_SHAPEFILE_STATUSES: ContestShapefileStatus[] = [
  'processing',
  'labeling',
  'creating'
];

export const isContestShapefileActive = (
  status?: ContestShapefileStatus
): boolean => Boolean(status && ACTIVE_CONTEST_SHAPEFILE_STATUSES.includes(status));

export const assignContestShapefileRowDistrict = (
  row: ContestShapefileRow,
  district: District
): ContestShapefileRow => ({
  ...row,
  match: {
    inputText: row.contest,
    districtId: district._id,
    district,
    confidence: 1,
    createWithData: null
  },
  included: true,
  reviewed: true,
  boundaryComparison: null,
  parentBoundaryComparison: null,
  boundaryAction: (
    district.hasGeodistrict === false
    && district.hasPartialGeodistrict === true
  ) ? 'append' : null,
  creationStatus: 'not-started',
  creationError: null
});

export const clearContestShapefileRowDistrict = (
  row: ContestShapefileRow
): ContestShapefileRow => ({
  ...row,
  match: {
    inputText: row.contest,
    districtId: null,
    district: null,
    confidence: null,
    createWithData: null
  },
  included: true,
  reviewed: true,
  boundaryComparison: null,
  parentBoundaryComparison: null,
  boundaryAction: null,
  creationStatus: 'not-started',
  creationError: null
});

export const confirmContestShapefileRowMatch = (
  row: ContestShapefileRow
): ContestShapefileRow => ({
  ...row,
  match: row.match ? {
    ...row.match,
    confidence: 1
  } : null,
  included: true,
  reviewed: true,
  creationStatus: 'not-started',
  creationError: null
});

export const setContestShapefileRowIncluded = (
  row: ContestShapefileRow,
  included: boolean
): ContestShapefileRow => ({
  ...row,
  included,
  reviewed: true,
  creationStatus: 'not-started',
  creationError: null
});

export const approveContestShapefileParentBoundary = (
  row: ContestShapefileRow
): ContestShapefileRow => ({
  ...row,
  parentBoundaryComparison: row.parentBoundaryComparison ? {
    ...row.parentBoundaryComparison,
    manuallyApproved: true,
    manuallyApprovedAt: new Date().toISOString()
  } : null
});

export const replaceContestShapefileRow = (
  rows: ContestShapefileRow[],
  updatedRow: ContestShapefileRow
): ContestShapefileRow[] => rows.map(row => (
  row.dataIndex === updatedRow.dataIndex ? updatedRow : row
));

export const contestShapefileRowAttentionMessage = (
  row: ContestShapefileRow
): string | null => {
  const messages: string[] = [];
  if (row.included && !row.match?.districtId) {
    messages.push('No district is matched to this contest.');
  }
  if (row.included && row.boundaryComparison?.significantDifference) {
    messages.push('The suggested boundary differs significantly from the existing district boundary.');
  }
  if (
    row.included
    && row.parentBoundaryComparison?.insufficientContainment
    && row.parentBoundaryComparison.manuallyApproved !== true
  ) {
    const containedPercent = Math.round(row.parentBoundaryComparison.containedRatio * 100);
    messages.push(
      `Only ${containedPercent}% of the new district boundary lies within its parent district.`
    );
  }
  if (row.creationError?.message) {
    messages.push(row.creationError.message);
  } else if (row.creationStatus === 'error') {
    messages.push('Geodistrict creation failed for this contest.');
  }
  return messages.length ? messages.join(' ') : null;
};

export const contestShapefileRowNeedsAttention = (
  row: ContestShapefileRow
): boolean => Boolean(contestShapefileRowAttentionMessage(row));

export const contestShapefileRowParentDistrictId = (
  row: ContestShapefileRow,
  stateDistrictId?: string
): string | null => {
  const createWithData = row.match?.createWithData;
  const parentDistrict = createWithData?.parentDistrict;
  if (
    !createWithData?.parentId
    || createWithData.parentId === stateDistrictId
    || (typeof parentDistrict !== 'string' && parentDistrict?.type === 'state')
  ) {
    return null;
  }
  return createWithData.parentId;
};

export interface ContestShapefileReviewValidation {
  includedCount: number;
  unresolvedCount: number;
  eligibleCreationCount: number;
  canCreate: boolean;
}

export const validateContestShapefileRows = (
  rows: ContestShapefileRow[]
): ContestShapefileReviewValidation => {
  const includedRows = rows.filter(row => row.included !== false);
  const unresolvedCount = includedRows.filter(row => !row.match?.districtId).length;
  const eligibleCreationCount = includedRows.filter(row => (
    Boolean(row.match?.districtId)
    && row.creationStatus !== 'success'
    && row.match?.district?.hasGeodistrict !== true
  )).length;

  return {
    includedCount: includedRows.length,
    unresolvedCount,
    eligibleCreationCount,
    canCreate:
      includedRows.length > 0 &&
      unresolvedCount === 0
  };
};
